/**
 * Canonical questionnaire automation timing.
 *
 * Offset is stored as minutes. Before-appointment automations are relative to
 * starts_at. If the calculated send time has already passed (booked inside the
 * send window), send immediately. After-appointment automations are relative to
 * completion time. Booking time is not a send trigger.
 */

export type QuestionnairePhase = 'before_appointment' | 'after_appointment';
export type TimingUnit = 'minutes' | 'hours' | 'days';
export type PreVisitDelayUnit = 'hours' | 'days';

export const MAX_OFFSET_MINUTES = 8760 * 60;

export type QuestionnaireAutomationDraft = {
  id: string;
  forms_template_id: string;
  phase: QuestionnairePhase;
  offset_minutes: number;
  delay_after_booking_minutes: number;
  is_active: boolean;
  sort_order: number;
};

export function clampOffsetMinutes(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(MAX_OFFSET_MINUTES, Math.trunc(value)));
}

export function displayToMinutes(value: number, unit: TimingUnit): number {
  const n = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
  switch (unit) {
    case 'minutes':
      return clampOffsetMinutes(n);
    case 'hours':
      return clampOffsetMinutes(n * 60);
    case 'days':
      return clampOffsetMinutes(n * 24 * 60);
    default: {
      const _exhaustive: never = unit;
      return _exhaustive;
    }
  }
}

export function parseOptionalTimingValue(raw: string): number | '' {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  return Math.max(0, Math.min(8760, Math.trunc(n)));
}

export function minutesToDisplay(minutes: number): { value: number; unit: TimingUnit } {
  const n = clampOffsetMinutes(minutes);
  if (n > 0 && n % (24 * 60) === 0) return { value: n / (24 * 60), unit: 'days' };
  if (n > 0 && n % 60 === 0) return { value: n / 60, unit: 'hours' };
  if (n === 0) return { value: 0, unit: 'hours' };
  return { value: n, unit: 'minutes' };
}

/** @deprecated Prefer minutesToDisplay. Kept for existing pre-visit hour fields. */
export function splitDelayHours(hours: number): { value: number; unit: PreVisitDelayUnit } {
  const display = minutesToDisplay(Math.max(0, hours) * 60);
  if (display.unit === 'days') return { value: display.value, unit: 'days' };
  return { value: display.unit === 'minutes' ? hours : display.value, unit: 'hours' };
}

export function computeQuestionnaireSendAt(input: {
  phase: QuestionnairePhase;
  anchorAt: Date;
  now: Date;
  offsetMinutes: number;
}): Date {
  const offset = clampOffsetMinutes(input.offsetMinutes);
  const now = input.now;

  switch (input.phase) {
    case 'before_appointment': {
      if (offset === 0) return now;
      const triggerAt = new Date(input.anchorAt.getTime() - offset * 60_000);
      if (now.getTime() >= triggerAt.getTime()) return now;
      return triggerAt;
    }
    case 'after_appointment': {
      if (offset === 0) return now;
      const sendAt = new Date(input.anchorAt.getTime() + offset * 60_000);
      return sendAt.getTime() <= now.getTime() ? now : sendAt;
    }
    default: {
      const _exhaustive: never = input.phase;
      return _exhaustive;
    }
  }
}

export function computePreVisitSendAt(input: {
  startsAt: Date;
  bookedAt: Date;
  now: Date;
  hoursBefore: number;
  delayAfterBookingHours: number;
}): Date {
  const hoursBefore = Math.max(0, input.hoursBefore);
  const delayAfter = Math.max(0, input.delayAfterBookingHours);
  const now = input.now;

  if (hoursBefore === 0 && delayAfter === 0) return now;

  const triggerAt = new Date(input.startsAt.getTime() - hoursBefore * 60 * 60_000);
  if (hoursBefore > 0 && now.getTime() >= triggerAt.getTime()) return now;

  let sendAt = hoursBefore > 0 ? triggerAt : now;
  if (delayAfter > 0) {
    const delayAt = new Date(input.bookedAt.getTime() + delayAfter * 60 * 60_000);
    sendAt = hoursBefore > 0 ? new Date(Math.max(sendAt.getTime(), delayAt.getTime())) : delayAt;
  }
  if (sendAt.getTime() >= input.startsAt.getTime()) {
    if (hoursBefore > 0) {
      sendAt = triggerAt;
      if (now.getTime() >= sendAt.getTime()) return now;
    } else {
      return now;
    }
  }
  return sendAt.getTime() <= now.getTime() ? now : sendAt;
}

export function sortAutomationsForDisplay(
  rows: QuestionnaireAutomationDraft[],
): QuestionnaireAutomationDraft[] {
  return [...rows].sort((a, b) => {
    if (a.phase !== b.phase) {
      return a.phase === 'before_appointment' ? -1 : 1;
    }
    if (a.offset_minutes !== b.offset_minutes) {
      return a.phase === 'before_appointment'
        ? b.offset_minutes - a.offset_minutes
        : a.offset_minutes - b.offset_minutes;
    }
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.forms_template_id.localeCompare(b.forms_template_id);
  });
}

export function createQuestionnaireAutomationDraft(input: {
  forms_template_id: string;
  phase: QuestionnairePhase;
  sort_order: number;
  offset_minutes?: number;
}): QuestionnaireAutomationDraft {
  const defaultOffset = input.phase === 'before_appointment' ? 2 * 24 * 60 : 2 * 60;
  return {
    id: crypto.randomUUID(),
    forms_template_id: input.forms_template_id,
    phase: input.phase,
    offset_minutes: clampOffsetMinutes(input.offset_minutes ?? defaultOffset),
    delay_after_booking_minutes: 0,
    is_active: true,
    sort_order: input.sort_order,
  };
}

export function automationsFromLegacyFields(input: {
  pre_visit_questionnaire_template_id?: string | null;
  pre_visit_hours_before?: number | '';
  pre_visit_delay_after_booking_hours?: number | '';
  auto_send_questionnaires?: string[];
}): QuestionnaireAutomationDraft[] {
  const rows: QuestionnaireAutomationDraft[] = [];
  const beforeId = input.pre_visit_questionnaire_template_id;
  if (beforeId) {
    const hours =
      typeof input.pre_visit_hours_before === 'number' ? input.pre_visit_hours_before : 0;
    rows.push(
      createQuestionnaireAutomationDraft({
        forms_template_id: beforeId,
        phase: 'before_appointment',
        sort_order: 0,
        offset_minutes: hours * 60,
      }),
    );
  }
  (input.auto_send_questionnaires ?? []).forEach((templateId, index) => {
    if (!templateId) return;
    rows.push(
      createQuestionnaireAutomationDraft({
        forms_template_id: templateId,
        phase: 'after_appointment',
        sort_order: index,
        offset_minutes: 0,
      }),
    );
  });
  return rows;
}

export function legacyFieldsFromAutomations(rows: QuestionnaireAutomationDraft[]): {
  pre_visit_questionnaire_template_id: string | null;
  pre_visit_hours_before: number;
  pre_visit_delay_after_booking_hours: number;
  auto_send_questionnaires: string[];
} {
  const before = sortAutomationsForDisplay(
    rows.filter((row) => row.phase === 'before_appointment' && row.is_active),
  )[0];
  const after = sortAutomationsForDisplay(
    rows.filter((row) => row.phase === 'after_appointment' && row.is_active),
  );
  return {
    pre_visit_questionnaire_template_id: before?.forms_template_id ?? null,
    pre_visit_hours_before: before ? Math.round(before.offset_minutes / 60) : 0,
    pre_visit_delay_after_booking_hours: 0,
    auto_send_questionnaires: after.map((row) => row.forms_template_id),
  };
}
